/*
 * PKI.Analyze — turn a parsed certificate into ranked, actionable findings.
 *
 * This is the layer that separates a decoder from a diagnostic. Every online
 * certificate viewer can print "Signature Algorithm: sha1WithRSAEncryption".
 * None of them say "browsers have rejected SHA-1 since 2017, this certificate
 * cannot work". The field dump is the easy half; knowing which fields are a
 * problem is the half that takes the user somewhere.
 *
 * The engine emits CODES, never sentences — all copy lives in the nine locale
 * files, keyed by code, with the numbers passed as parameters. Adding a rule
 * therefore costs one entry here and nine short strings, and never leaves a
 * hard-coded English fragment on a Korean page.
 *
 * severity: 'error'   the certificate will not work, or is unsafe
 *           'warning' works today, will bite later or in some clients
 *           'info'    worth knowing, not a defect
 *           'good'    an explicit positive, so a clean cert does not look empty
 */
(function (root) {
    'use strict';

    var P = (root && root.PKI) || {};
    var X509 = P.X509 || (typeof require !== 'undefined' ? require('./x509.js') : null);

    /**
     * Browser-enforced maximum certificate lifetime. The CA/Browser Forum
     * schedule (ballot SC-081) steps this down over several years; certificates
     * are judged against the limit in force when they were ISSUED, which is why
     * this is a table and not a constant.
     *
     * ⚠️ Re-check against the current CA/B baseline requirements before trusting
     * the later rows — they are dated and will move.
     */
    var LIFETIME_LIMITS = [
        { from: Date.UTC(2029, 2, 15), days: 47 },
        { from: Date.UTC(2027, 2, 15), days: 100 },
        { from: Date.UTC(2026, 2, 15), days: 200 },
        { from: Date.UTC(2020, 8, 1),  days: 398 },
        { from: 0,                     days: 825 }
    ];

    function lifetimeLimitFor(notBefore) {
        var t = notBefore ? notBefore.getTime() : Date.now();
        for (var i = 0; i < LIFETIME_LIMITS.length; i++) {
            if (t >= LIFETIME_LIMITS[i].from) return LIFETIME_LIMITS[i].days;
        }
        return 825;
    }

    // Names a public CA cannot issue for. Reserved TLDs (RFC 2606/6761) plus the
    // de-facto private ones; internal names stopped being issuable in Nov 2015.
    var INTERNAL_TLDS = ['local', 'localdomain', 'internal', 'intranet', 'lan', 'corp',
        'home', 'private', 'test', 'example', 'invalid', 'localhost'];

    // Suffixes where a wildcard would cover an entire registry.
    var PUBLIC_SUFFIX_SAMPLE = ['com', 'net', 'org', 'io', 'co', 'ru', 'de', 'uk', 'co.uk',
        'com.au', 'co.jp', 'com.br', 'edu', 'gov'];

    /**
     * @param cert  parsed X509
     * @param opts  {now, hostname, isChainRoot, chainComplete, publiclyIssued}
     */
    function certificate(cert, opts) {
        opts = opts || {};
        var now = opts.now ? new Date(opts.now) : new Date();
        var f = [];
        var add = function (code, severity, params) { f.push({ code: code, severity: severity, params: params || {} }); };

        // Is this meant to be a public web certificate? Private CA output is
        // judged by different rules, and blaming a homelab cert for having no
        // Certificate Transparency proof would be noise, not a finding.
        var isPublic = opts.publiclyIssued !== undefined ? opts.publiclyIssued
            : (!cert.selfIssued && (cert.scts.length > 0 || cert.validationLevel !== null || cert.ocspUrls.length > 0));

        /* ── validity window ─────────────────────────────────────── */
        var msLeft = cert.notAfter ? cert.notAfter.getTime() - now.getTime() : null;
        var daysLeft = msLeft === null ? null : Math.floor(msLeft / 86400000);

        if (cert.notBefore && cert.notBefore.getTime() > now.getTime()) {
            add('not_yet_valid', 'error', { date: cert.notBefore, days: Math.ceil((cert.notBefore - now) / 86400000) });
        }
        if (daysLeft !== null && daysLeft < 0) {
            add('expired', 'error', { date: cert.notAfter, days: Math.abs(daysLeft) });
        } else if (daysLeft !== null && daysLeft <= 7) {
            add('expiring_critical', 'error', { days: daysLeft, date: cert.notAfter });
        } else if (daysLeft !== null && daysLeft <= 30) {
            add('expiring_soon', 'warning', { days: daysLeft, date: cert.notAfter });
        } else if (daysLeft !== null) {
            add('valid_until', 'good', { days: daysLeft, date: cert.notAfter });
        }

        if (isPublic && cert.validityDays) {
            var limit = lifetimeLimitFor(cert.notBefore);
            if (cert.validityDays > limit) {
                add('lifetime_too_long', 'error', { days: cert.validityDays, limit: limit });
            }
        }

        /* ── signature and key strength ──────────────────────────── */
        var hash = cert.signatureHash;
        if (hash === 'MD5' || hash === 'MD2' || hash === 'MD4') {
            add('signature_md5', 'error', { algorithm: cert.signatureAlgorithm });
        } else if (hash === 'SHA-1') {
            // Self-signed roots are exempt: a root's signature is never checked,
            // and every trust store still ships SHA-1 roots.
            add(cert.selfIssued && cert.isCa ? 'signature_sha1_root' : 'signature_sha1',
                cert.selfIssued && cert.isCa ? 'info' : 'error',
                { algorithm: cert.signatureAlgorithm });
        }

        var pk = cert.publicKey;
        if (cert.publicKeyError) {
            add('public_key_unreadable', 'error', { reason: cert.publicKeyError });
        } else if (pk) {
            if (pk.kind === 'RSA') {
                if (pk.bits < 1024) add('rsa_key_tiny', 'error', { bits: pk.bits });
                else if (pk.bits < 2048) add('rsa_key_too_small', 'error', { bits: pk.bits });
                else if (pk.bits === 2048) add('rsa_key_2048', 'good', { bits: pk.bits });
                else add('rsa_key_strong', 'good', { bits: pk.bits });

                if (pk.rsa && pk.rsa.exponentValue !== null && pk.rsa.exponentValue < 65537) {
                    add('rsa_exponent_small', 'warning', { exponent: pk.rsa.exponentValue });
                }
            } else if (pk.kind === 'EC') {
                if (pk.bits && pk.bits < 256) add('ec_curve_weak', 'error', { curve: pk.curve, bits: pk.bits });
                else add('ec_key_strong', 'good', { curve: pk.curve, bits: pk.bits });
                if (!pk.webcryptoCurve) add('ec_curve_uncommon', 'info', { curve: pk.curve });
            } else if (pk.kind === 'Ed25519') {
                // Correct choice cryptographically, but no public CA issues it
                // and TLS stacks vary — worth flagging so nobody is surprised.
                add('ed25519_key', 'info', {});
            } else if (pk.kind === 'DSA') {
                add('dsa_key', 'error', { bits: pk.bits });
            }
        }

        /* ── names ───────────────────────────────────────────────── */
        var cn = cert.subject.get('CN');
        // Name quality is checked whenever the certificate carries DNS names,
        // CA flag or not: `openssl req -x509` produces CA:TRUE by default, so
        // the most common home-made server certificate IS a CA on paper, and
        // skipping its name checks would silently drop the findings that
        // actually matter to whoever pasted it.
        if (!cert.isCa || cert.dnsNames.length) {
            if (!cert.isCa && !cert.sanEntries.length) {
                add('no_san', 'error', { cn: cn });
            }
            if (cert.sanEntries.length) {
                if (cn && cert.dnsNames.length &&
                    !cert.dnsNames.some(function (d) { return d.toLowerCase() === cn.toLowerCase(); }) &&
                    !cert.ipAddresses.some(function (ip) { return ip === cn; })) {
                    add('cn_not_in_san', 'warning', { cn: cn });
                }
                var lower = cert.dnsNames.map(function (d) { return d.toLowerCase(); });
                var dupes = lower.filter(function (d, i) { return lower.indexOf(d) !== i; });
                if (dupes.length) add('duplicate_san', 'info', { names: uniq(dupes) });

                cert.dnsNames.forEach(function (name) {
                    var n = name.toLowerCase();
                    if (n.indexOf('*') >= 0) {
                        var bare = n.replace(/^\*\./, '');
                        if (n.indexOf('*.') !== 0) {
                            add('wildcard_bad_position', 'error', { name: name });
                        } else if (PUBLIC_SUFFIX_SAMPLE.indexOf(bare) >= 0 || bare.indexOf('.') < 0) {
                            add('wildcard_too_broad', 'error', { name: name });
                        }
                    }
                    var tld = n.split('.').pop();
                    if (n.indexOf('.') < 0) {
                        add('single_label_name', 'warning', { name: name });
                    } else if (INTERNAL_TLDS.indexOf(tld) >= 0) {
                        add('internal_name', 'warning', { name: name, tld: tld });
                    }
                    if (n.indexOf('_') >= 0) add('underscore_in_name', 'warning', { name: name });
                });

                if (cert.ipAddresses.length && isPublic) {
                    add('ip_in_san', 'info', { addresses: cert.ipAddresses });
                }
                if (cert.dnsNames.length > 100) {
                    add('many_san', 'info', { count: cert.dnsNames.length });
                }
            }
        }

        if (opts.hostname) {
            var m = X509.matchesHostname(cert, opts.hostname);
            if (m.matched) add('hostname_match', 'good', { hostname: opts.hostname, via: m.via, name: m.name });
            else if (m.reason === 'cn-only') add('hostname_cn_only', 'error', { hostname: opts.hostname, cn: m.cn });
            else add('hostname_mismatch', 'error', { hostname: opts.hostname, names: cert.dnsNames });
        }

        /* ── constraints and usage ───────────────────────────────── */
        if (!cert.hasBasicConstraints) {
            add('no_basic_constraints', 'warning', {});
        }
        if (cert.isCa && !cert.selfIssued && !opts.expectCa) {
            add('ca_true', 'info', { pathLen: cert.pathLen });
        }
        if (cert.isCa) {
            if (cert.keyUsage.length && cert.keyUsage.indexOf('keyCertSign') < 0) {
                add('ca_without_cert_sign', 'error', {});
            }
        } else {
            if (cert.keyUsage.length && cert.keyUsage.indexOf('keyCertSign') >= 0) {
                add('leaf_with_cert_sign', 'error', {});
            }
            if (cert.extKeyUsage.length && !cert.isServerAuth) {
                add('no_server_auth', 'error', {
                    usages: cert.extKeyUsage.map(function (e) { return e.name; })
                });
            }
            if (!cert.extKeyUsage.length) {
                add('no_eku', 'info', {});
            }
            if (cert.keyUsage.length && cert.keyUsage.indexOf('digitalSignature') < 0) {
                // Without digitalSignature the certificate cannot be used with
                // any ECDHE suite — i.e. with TLS 1.3 at all.
                add('no_digital_signature', 'warning', {});
            }
        }

        /* ── public-CA hygiene ───────────────────────────────────── */
        if (isPublic) {
            if (!cert.scts.length && !cert.isCa) {
                add('no_sct', 'warning', {});
            } else if (cert.scts.length) {
                add('sct_present', 'good', { count: cert.scts.length });
            }
            if (cert.serialBits < 64) {
                add('serial_low_entropy', 'warning', { bits: cert.serialBits });
            }
        }
        if (cert.selfIssued && !cert.isCa) {
            add('self_signed', 'warning', {});
        } else if (cert.selfIssued && cert.isCa) {
            add('self_signed_ca', 'info', {});
        }
        if (cert.mustStaple) add('must_staple', 'info', {});
        if (cert.validationLevel) add('validation_level', 'info', { level: cert.validationLevel });
        if (cert.algorithmMismatch) add('algorithm_mismatch', 'error', {});

        var criticalUnknown = cert.extensions.filter(function (e) {
            return e.critical && e.value === null && !e.decodeError;
        });
        if (criticalUnknown.length) {
            add('unknown_critical_extension', 'warning', {
                oids: criticalUnknown.map(function (e) { return e.oid; })
            });
        }

        if (opts.chainComplete === false) add('chain_incomplete', 'error', {});

        return { findings: f, verdict: verdictOf(f), isPublic: isPublic, daysRemaining: daysLeft };
    }

    /* ═══ CSR ═══════════════════════════════════════════════════════ */

    function csr(req, opts) {
        opts = opts || {};
        var f = [];
        var add = function (code, severity, params) { f.push({ code: code, severity: severity, params: params || {} }); };

        if (opts.signatureValid === false) add('csr_bad_signature', 'error', {});
        else if (opts.signatureValid === true) add('csr_signature_ok', 'good', {});

        var hash = req.signatureHash;
        if (hash === 'SHA-1' || hash === 'MD5') add('csr_weak_hash', 'error', { algorithm: req.signatureAlgorithm });

        var pk = req.publicKey;
        if (pk) {
            if (pk.kind === 'RSA') {
                if (pk.bits < 2048) add('rsa_key_too_small', 'error', { bits: pk.bits });
                else add(pk.bits === 2048 ? 'rsa_key_2048' : 'rsa_key_strong', 'good', { bits: pk.bits });
            } else if (pk.kind === 'EC') {
                if (pk.bits && pk.bits < 256) add('ec_curve_weak', 'error', { curve: pk.curve, bits: pk.bits });
                else add('ec_key_strong', 'good', { curve: pk.curve, bits: pk.bits });
            }
        }

        var cn = req.subject.get('CN');
        if (!cn && !req.dnsNames.length) {
            add('csr_no_name', 'error', {});
        }
        // The single most consequential CSR defect: no SAN. Public CAs ignore CN
        // entirely, so such a request either gets rejected or is silently
        // "fixed" by the CA adding the CN as a SAN — and the user never learns.
        if (!req.dnsNames.length && !req.ipAddresses.length) {
            add('csr_no_san', 'warning', { cn: cn });
        } else if (cn && !req.dnsNames.some(function (d) { return d.toLowerCase() === String(cn).toLowerCase(); })) {
            add('cn_not_in_san', 'warning', { cn: cn });
        }

        req.dnsNames.forEach(function (name) {
            var n = name.toLowerCase(), tld = n.split('.').pop();
            if (n.indexOf('.') < 0) add('single_label_name', 'warning', { name: name });
            else if (INTERNAL_TLDS.indexOf(tld) >= 0) add('internal_name', 'warning', { name: name, tld: tld });
            if (n.indexOf('_') >= 0) add('underscore_in_name', 'warning', { name: name });
            if (n.indexOf('*') === 0 && (n.replace(/^\*\./, '').indexOf('.') < 0 ||
                PUBLIC_SUFFIX_SAMPLE.indexOf(n.replace(/^\*\./, '')) >= 0)) {
                add('wildcard_too_broad', 'error', { name: name });
            }
        });

        var c = req.subject.get('C');
        if (c && c.length !== 2) add('csr_bad_country', 'error', { value: c });
        if (req.challengePassword) add('csr_challenge_password', 'info', {});
        if (req.requestedCa) add('csr_requests_ca', 'warning', {});

        return { findings: f, verdict: verdictOf(f) };
    }

    function verdictOf(findings) {
        if (findings.some(function (x) { return x.severity === 'error'; })) return 'error';
        if (findings.some(function (x) { return x.severity === 'warning'; })) return 'warning';
        return 'ok';
    }

    function uniq(a) {
        return a.filter(function (v, i) { return a.indexOf(v) === i; });
    }

    /** Sort for display: errors first, then warnings, info, and positives last. */
    function rank(findings) {
        var order = { error: 0, warning: 1, info: 2, good: 3 };
        return findings.slice().sort(function (a, b) {
            return (order[a.severity] - order[b.severity]);
        });
    }

    var API = {
        certificate: certificate, csr: csr, rank: rank, verdictOf: verdictOf,
        lifetimeLimitFor: lifetimeLimitFor,
        LIFETIME_LIMITS: LIFETIME_LIMITS, INTERNAL_TLDS: INTERNAL_TLDS
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) { root.PKI = root.PKI || {}; root.PKI.Analyze = API; }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
